import { PhUserMinus } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { executeSearchRequest } from 'tio-data/requests/search';
import { buildApprovalQueueSearch } from 'tio-employee/utils/tas/approval-queue-search';
import { oncePerVisit } from 'tio-employee/utils/partner-dashboard/memo';
import {
  QUEUE_PREVIEW_SIZE,
  daysWaiting,
  toQueueItem,
  type QueueDocument,
} from 'tio-employee/utils/partner-dashboard/sources/approval-queue';
import {
  ALL_PENDING_QUEUE_QUERY,
  hasTuitionAssistance,
} from 'tio-employee/utils/partner-dashboard/sources/ta-analytics';
import type {
  AttentionItem,
  AttentionSource,
  DashboardContext,
} from 'tio-employee/utils/partner-dashboard/registry';

/** Reviewers per page of the composite reviewer aggregation. */
export const REVIEWER_PAGE = 500;

// A hard stop on paging (10,000 reviewers per record type), so a bad after-key can't loop forever.
const MAX_REVIEWER_PAGES = 20;

// Ids per employee lookup: well under the API's 400-record page cap and a long query string.
const LOOKUP_BATCH = 100;

const APPLICATION_REVIEWERS = 'tas_application_current_reviewers_keyword';
const PROGRAM_REVIEWERS = 'tas_program_instance_current_reviewers_keyword';

/** Pending queue items waiting on one reviewer's current step. */
export interface ReviewerLoad {
  employeeId: string;
  /** Empty for a reviewer outside the company (see `isActive`). */
  name: string;
  /**
   * Null for a reviewer the company-scoped employee lookup can't see, typically
   * a Tuition.io reviewer employed by Tuition.io. They are never counted as stranded.
   */
  isActive: boolean | null;
  pending: number;
}

interface ReviewerBucket {
  key?: { reviewer?: string | number };
  doc_count?: number;
  docCount?: number;
}

interface ReviewerAggregation {
  buckets?: ReviewerBucket[];
  after_key?: Record<string, unknown>;
  afterKey?: Record<string, unknown>;
}

// Application and program-instance docs share the index; each is counted on its own reviewer field.
const reviewerScope = (ids: string[]) => ({
  bool: {
    should: [
      {
        bool: {
          filter: [
            { term: { record_type: 'application' } },
            { terms: { [APPLICATION_REVIEWERS]: ids } },
          ],
        },
      },
      {
        bool: {
          filter: [{ term: { record_type: 'instance' } }, { terms: { [PROGRAM_REVIEWERS]: ids } }],
        },
      },
    ],
    minimum_should_match: 1,
  },
});

/** The queue search for every pending item at the company, ignoring whose turn it is. */
function pendingQueueBody(ctx: DashboardContext, limit: number) {
  return buildApprovalQueueSearch({
    companyId: ctx.companyId,
    reviewerId: '',
    disableReviewer: true,
    order: 'first_submitted_asc',
    limit,
  })['search-body'] as unknown as {
    query: { bool: { filter: Record<string, unknown>[] } };
    [key: string]: unknown;
  };
}

// Every reviewer, paged with a composite aggregation; a terms aggregation would silently drop the tail.
async function reviewerCounts(
  ctx: DashboardContext,
  store: Store,
  recordType: 'application' | 'instance',
  field: string
) {
  const counts = new Map<string, number>();
  let after: Record<string, unknown> | undefined;
  for (let page = 0; page < MAX_REVIEWER_PAGES; page++) {
    const body = pendingQueueBody(ctx, 0);
    body.query.bool.filter.push({ term: { record_type: recordType } });
    body['aggs'] = {
      reviewers: {
        composite: {
          size: REVIEWER_PAGE,
          sources: [{ reviewer: { terms: { field } } }],
          ...(after ? { after } : {}),
        },
      },
    };
    const { content } = await store.request<OpenSearchResponse>(
      executeSearchRequest({ 'model-type': 'TasProgramInstance', 'search-body': body })
    );
    const aggregation = (content.aggregations as Record<string, unknown> | undefined)?.[
      'reviewers'
    ] as ReviewerAggregation | undefined;
    const buckets = (aggregation?.buckets ?? []).filter(Boolean);
    for (const bucket of buckets) {
      const id = bucket.key?.reviewer;
      if (id === undefined || id === '') continue;
      counts.set(String(id), bucket.doc_count ?? bucket.docCount ?? 0);
    }
    after = aggregation?.after_key ?? aggregation?.afterKey;
    if (buckets.length < REVIEWER_PAGE || !after) break;
  }
  return counts;
}

/**
 * Pending items per current reviewer across the company, heaviest first,
 * with each reviewer's name and whether they are still active. A paged queue
 * aggregation per record type plus batched employee lookups, shared per visit by the workload card
 * and the stranded-approvals source. It counts the same items the approval
 * queue shows with every reviewer included; an item waiting on two reviewers
 * counts for both.
 */
export const loadReviewerLoad = oncePerVisit(async (ctx): Promise<ReviewerLoad[]> => {
  if (!ctx.companyId) return [];
  const store = ctx.owner.lookup('service:store') as Store;
  const pending = new Map<string, number>();
  const counts = await Promise.all([
    reviewerCounts(ctx, store, 'application', APPLICATION_REVIEWERS),
    reviewerCounts(ctx, store, 'instance', PROGRAM_REVIEWERS),
  ]);
  for (const [id, count] of counts.flatMap((map) => [...map])) {
    pending.set(id, (pending.get(id) ?? 0) + count);
  }
  if (!pending.size) return [];

  const ids = [...pending.keys()];
  const batches = Array.from({ length: Math.ceil(ids.length / LOOKUP_BATCH) }, (_, i) =>
    ids.slice(i * LOOKUP_BATCH, (i + 1) * LOOKUP_BATCH)
  );
  const found = await Promise.all(
    batches.map(async (batch) => {
      const { content: employees } = await store.request<{ data: EmployeeModel[] }>(
        query<EmployeeModel>('employee', {
          'filter[company_id]': ctx.companyId,
          'filter[id]': batch.join(','),
          // The employee index defaults to active employees; deactivated reviewers are the point here.
          'filter[is_active]': 'true,false',
          'page[limit]': batch.length,
        })
      );
      return employees.data;
    })
  );
  const byId = new Map(found.flat().map((employee) => [String(employee.id), employee]));
  return ids
    .map((employeeId) => {
      const employee = byId.get(employeeId);
      return {
        employeeId,
        name: employee?.fullName ?? '',
        isActive: employee ? Boolean(employee.isActive) : null,
        pending: pending.get(employeeId) ?? 0,
      };
    })
    .sort((a, b) => b.pending - a.pending || a.name.localeCompare(b.name));
});

interface StrandedDocument extends QueueDocument {
  tasApplicationCurrentReviewersKeyword?: string | string[];
  tasProgramInstanceCurrentReviewersKeyword?: string | string[];
}

const asList = (value: string | string[] | undefined) =>
  value === undefined ? [] : Array.isArray(value) ? value : [value];

/**
 * Needs Your Attention rows for pending items whose current step waits on a
 * deactivated reviewer, oldest first, plus an "N more" row. Each row opens the
 * item's review page, where an admin can add another reviewer. Only the
 * current step is checked: an item that will reach a deactivated reviewer at
 * a later step isn't stranded yet.
 */
export const STRANDED_APPROVALS_SOURCE: AttentionSource = {
  id: 'stranded-approvals',
  drivenBy: ['accountOwner', 'partnerAdmin'],
  requires: hasTuitionAssistance,
  load: async (ctx) => {
    const reviewers = await loadReviewerLoad(ctx);
    const deactivated = new Map(
      reviewers.filter((r) => r.isActive === false).map((r) => [r.employeeId, r.name])
    );
    if (!deactivated.size) return [];

    const store = ctx.owner.lookup('service:store') as Store;
    const intl = ctx.owner.lookup('service:intl') as IntlService;
    const body = pendingQueueBody(ctx, QUEUE_PREVIEW_SIZE);
    body.query.bool.filter.push(reviewerScope([...deactivated.keys()]));
    const { content } = await store.request<OpenSearchResponse>(
      executeSearchRequest({ 'model-type': 'TasProgramInstance', 'search-body': body })
    );
    const page = OpenSearchResponseBuilder.from(content).toPaginatedResult();

    const rows: AttentionItem[] = (page.items as StrandedDocument[]).map((doc) => {
      const item = toQueueItem(doc);
      const reviewerIds = asList(
        item.kind === 'program'
          ? doc.tasProgramInstanceCurrentReviewersKeyword
          : doc.tasApplicationCurrentReviewersKeyword
      );
      const names = reviewerIds
        .filter((id) => deactivated.has(id))
        .map((id) => deactivated.get(id) || intl.t('partner_dashboard.stranded.unknown_approver'));
      const approvers = names.length
        ? names
        : [intl.t('partner_dashboard.stranded.unknown_approver')];
      const days = daysWaiting(item.waitingSince);
      return {
        id: `stranded-approvals:${item.id}`,
        title: intl.t(`partner_dashboard.stranded.title_${item.kind}`, { name: item.employeeName }),
        description: intl.t('partner_dashboard.stranded.description', {
          approvers: intl.formatList(approvers, { type: 'conjunction' }),
          count: approvers.length,
        }),
        icon: PhUserMinus,
        urgency: 'urgent',
        dueAt: item.waitingSince,
        ...(days === null
          ? {}
          : { dueLabel: intl.t('partner_dashboard.queue.waiting_days', { count: days }) }),
        action: {
          label: intl.t('partner_dashboard.stranded.reassign_action'),
          route: item.reviewRoute,
          model: item.reviewModel,
        },
        pii: true,
      };
    });

    const more = page.total - rows.length;
    if (more > 0) {
      rows.push({
        id: 'stranded-approvals:more',
        title: intl.t('partner_dashboard.stranded.more_title', { count: more }),
        description: intl.t('partner_dashboard.stranded.more_description'),
        icon: PhUserMinus,
        urgency: 'urgent',
        action: {
          label: intl.t('partner_dashboard.sla.view_action'),
          route: 'authenticated.admin.tas.approval-queue',
          query: ALL_PENDING_QUEUE_QUERY,
        },
      });
    }
    return rows;
  },
};
